import { useState } from "react";
import { ViewId } from "@msidentity/SISU/constants";
import { useNavigateDirection, useRedirect } from "@msidentity/SISU/hooks";
import UsernameRecoveryConfig from "../username-recovery-config";
import {
  type UsernameRecoveryProofState,
  useUsernameRecoveryContext,
} from "../username-recovery-context";
import { UsernameRecoveryActionType } from "../username-recovery-reducer";
import { UsernameRecoveryResultAliasType } from "../username-recovery-types";
import { emailAliasHandler, phoneAliasHandler } from "../utilities/select-alias-utilities";
import { type SelectedRecoveryAlias } from "../views/select-alias/username-recovery-select-alias-view-types";
import { type SignInPostData, useSignInPostRedirect } from "./use-signin-post-redirect";

/**
 * Custom hook for alias handlers.
 * @param filteredAccountsForProofResult - filtered accounts for proof result
 * @param selectedAlias - selected alias
 * @param setSelectedAlias - set selected alias
 * @param isGenericServiceError - boolean indicating if the accounts for proof result contains both masked and unmasked aliases
 * @returns validation handlers for alias
 */
export const useSelectAliasHandlers = function useSelectAliasHandlers(
  filteredAccountsForProofResult: UsernameRecoveryProofState,
  selectedAlias: SelectedRecoveryAlias | undefined,
  setSelectedAlias: (selectedAlias: SelectedRecoveryAlias | undefined) => void,
  isGenericServiceError: boolean,
) {
  const [aliasInputValue, setAliasInputValue] = useState("");
  const [error, setError] = useState("");
  const redirect = useRedirect();
  const signInPostRedirect = useSignInPostRedirect();
  const navigate = useNavigateDirection();

  const { cancelUrl, signInPostRedirectUrl } = UsernameRecoveryConfig.instance;
  const {
    viewState: { shortLivedToken },
    dispatchStateChange,
  } = useUsernameRecoveryContext();
  const noAliases = !filteredAccountsForProofResult.aliases.length;

  const onCancelHandler = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (cancelUrl) {
      redirect(cancelUrl);
    }
  };

  const handleAliasSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (noAliases) {
      return;
    }

    const selectedAliasIndex = filteredAccountsForProofResult.aliases.findIndex(
      (alias, index) => `${alias.name}${index}` === value,
    );
    const selectedAliasMethod =
      selectedAliasIndex !== -1
        ? filteredAccountsForProofResult.aliases[selectedAliasIndex]
        : undefined;

    if (selectedAliasMethod) {
      setError("");
      setAliasInputValue("");
      setSelectedAlias({
        alias: selectedAliasMethod,
        index: selectedAliasIndex,
      });
    }
  };

  const handleAliasInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setAliasInputValue(value);
    setError("");
  };

  const onSubmitHandler = (event: React.SyntheticEvent) => {
    event.preventDefault();

    if (noAliases || isGenericServiceError) {
      // If there are no associated accounts, we'll clear the prefill so the user isn't stuck in a loop
      dispatchStateChange({ type: UsernameRecoveryActionType.SetHasPrefill, hasPrefill: false });
      navigate(ViewId.UsernameRecoverySelectAlias, ViewId.UsernameRecoveryCollect);
      return;
    }

    const aliasSelected = selectedAlias?.alias || filteredAccountsForProofResult.aliases[0];

    const handler =
      aliasSelected.type === UsernameRecoveryResultAliasType.Email
        ? emailAliasHandler(aliasInputValue, aliasSelected.name)
        : phoneAliasHandler(aliasInputValue, aliasSelected.name);

    if (!aliasSelected.isUnmasked && !handler.validate()) {
      setError(handler.error());
      return;
    }

    // redirect to signin with slt and alias
    const postData: SignInPostData = {
      slt: shortLivedToken,
      alias: aliasSelected.isUnmasked ? aliasSelected.name : aliasInputValue,
    };
    signInPostRedirect(signInPostRedirectUrl, postData);
  };

  return {
    aliasInputValue,
    error,
    onCancelHandler,
    handleAliasSelection,
    handleAliasInputChange,
    onSubmitHandler,
  };
};
